import { useState } from 'react'

import { ReviewWriteCheckIcon, CommonHorizontalArrow2Icon, CommonHorizontalArrow7Icon } from '@local/assets/icons'

import FlickingArea from '@web/components/FlickingArea/FlickingArea'
import { convertIntoDisplayCount } from '@web/libs/formatter'
import Clickable from '@web/ui/components/Clickable'
import UIRecentOrderAndBookingCard, {
  UIRecentOrderAndBookingCardProps,
} from '@web/ui/pages/home/components/Sections/RecentOrderAndBookingSection/Card'
import {
  RecentBookingOrderTab,
  recentBookingOrderTabs,
} from '@web/ui/pages/home/components/Sections/RecentOrderAndBookingSection/constant'

import { Section, SectionHeader, SectionContent } from '../Section'
import styles from './RecentOrderAndBookingSection.module.scss'

interface UIRecentOrderAndBookingSectionProps {
  orderCards: UIRecentOrderAndBookingCardProps[]
  bookingCards: UIRecentOrderAndBookingCardProps[]
  stampCount: number
  maxDisplayStampCount?: number
  onClickStamp?: () => void
  onClickNearByStore?: () => void
  displayMaxLength?: number
  orderTabClickCode?: string
  bookingTabClickCode?: string
  findNearByClickCode?: string
  stampClickCode?: string
}

function UIRecentOrderAndBookingSection({
  orderCards,
  bookingCards,
  stampCount,
  onClickStamp,
  onClickNearByStore,
  maxDisplayStampCount = 999,
  orderTabClickCode,
  bookingTabClickCode,
  findNearByClickCode,
  stampClickCode,
}: UIRecentOrderAndBookingSectionProps) {
  const hasBooking = bookingCards.length > 0
  const hasOrder = orderCards.length > 0
  const hasStamp = stampCount > 0

  const shouldDisplayOrder = hasStamp || hasOrder
  const [tab, setTab] = useState<RecentBookingOrderTab>(shouldDisplayOrder ? '주문' : '예약')

  const title = `자주 찾는 ${shouldDisplayOrder && hasBooking ? '주문 / 예약' : hasBooking ? '예약' : '주문'}`

  if (!shouldDisplayOrder && !hasBooking) return null

  const displayCards = tab === '예약' ? bookingCards : orderCards

  return (
    <Section className={styles.recent_order_booking_section}>
      <SectionHeader title={title} />
      <SectionContent>
        {/* 탭 영역 */}
        {shouldDisplayOrder && hasBooking && (
          <div className={styles.tab_area} role="tablist">
            {recentBookingOrderTabs.map(_tab => (
              <Clickable
                key={_tab}
                className={styles.tab}
                role="tab"
                aria-selected={_tab === tab}
                onClick={() => setTab(_tab)}
                nClickCode={_tab === '예약' ? bookingTabClickCode : orderTabClickCode}
              >
                <span className={styles.name}>{_tab}</span>
              </Clickable>
            ))}
          </div>
        )}
        {/* 컨텐츠 플리킹 영역 */}
        {displayCards.length > 0 ? (
          <FlickingArea
            className={styles.flicking_area}
            options={{ cameraTag: 'ul', align: 'prev' }}
            arrowOption={{ y: 78 }}
          >
            {displayCards.map((card, index) => (
              <UIRecentOrderAndBookingCard key={index} {...card} nClickRank={index + 1} />
            ))}
          </FlickingArea>
        ) : (
          // 예약탭은 비어있는 경우가 없으므로 주문탭인 경우만 상정
          <div className={styles.empty_area}>
            <div className={styles.tit_area}>최근 주문 내역이 없습니다.</div>
            <div className={styles.txt_area}>근처 주문 가능한 매장을 찾아보세요!</div>
            <Clickable className={styles.btn_find} onClick={onClickNearByStore} nClickCode={findNearByClickCode}>
              근처 매장 찾기
              <CommonHorizontalArrow7Icon className={styles.icon} aria-hidden="true" />
            </Clickable>
          </div>
        )}

        {/* 적립스템프 확인버튼 */}
        {shouldDisplayOrder && hasStamp && tab === '주문' && (
          <Clickable className={styles.btn_stamp} onClick={onClickStamp} nClickCode={stampClickCode}>
            <div className={styles.row_wrapper}>
              <span className={styles.icon_wrapper}>
                <ReviewWriteCheckIcon className={styles.icon} />
              </span>
              적립중인 스탬프&nbsp;
              <span className={styles.color_txt}>
                <em>{convertIntoDisplayCount({ count: stampCount, maxDisplayCount: maxDisplayStampCount })}</em>개
              </span>
            </div>
            <CommonHorizontalArrow2Icon className={styles.icon_arrow} />
          </Clickable>
        )}
      </SectionContent>
    </Section>
  )
}

export default UIRecentOrderAndBookingSection
